<template>
  <div v-show="initialized" class="editor-grid tw-flex tw-flex-col tw-min-h-[100dvh] tw-overflow-hidden"
    :class="{ 'editor--panel-open': mobilePanelOpen }" ref="editorContainer"
    :style="isMobileViewport ? cardPanelOpenOffsetStyle : {}">
    <!-- Header: logo, side toggle (desktop), close; on mobile: close + Add to Cart -->
    <header ref="headerRef" class="tw-flex-shrink-0 tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-3 lg:tw-px-6 lg:tw-py-3">
      <!-- Left: Cart (if order) + Logo (desktop); on mobile only Close goes left, so logo/cart in center or hidden -->
      <div class="tw-flex tw-items-center">
        <div v-if="isEditOrder" id="cartButton"
          class="tw-bg-gray-700 tw-rounded-full tw-cursor-pointer tw-relative tw-w-10 tw-h-10 tw-items-center tw-justify-center hover:tw-bg-gray-600 tw-transition-colors tw-max-lg:tw-hidden lg:tw-flex"
          @click="productsVisible = true">
          <img :src="getAssetPath('/assets/images/cart.png')" :alt="$t('cart')" class="tw-size-6" />

          <span
            class="tw-absolute tw-top-[-7px] tw-right-[-7px] tw-bg-solYellow tw-text-black tw-p-[2px] tw-rounded-full tw-text-xs tw-w-4 tw-h-4 tw-text-center tw-font-semibold">{{
              numberOfProducts }}</span>
        </div>

        <div class="tw-w-[85px] tw-hidden lg:tw-block">
          <img :src="getAssetPath('/assets/images/solflareIcon.svg')" alt="Solflare" class=" tw-size-16" />
        </div>

        <EditorCloseButton v-if="editorMode === 'addToCart'" class="lg:tw-hidden" @close="onCloseClick" />
      </div>

      <!-- Center: Card Side Toggle with live preview (desktop only) -->
      <div class="tw-hidden lg:tw-block">
        <SideToggleWithPreview :front-preview-url="previewFrontUrl" :back-preview-url="previewBackUrl"
          :is-flipped="isFlipped" @select-front="showFrontSide" @select-back="showBackSide" />
      </div>

      <!-- Right: Add to Cart (mobile only), Close (desktop), Actions -->
      <div class="tw-flex tw-items-center tw-gap-4">
        <!-- Mobile: Add to Cart in header -->
        <SaveOrAddToCartButton :text="saveButtonText" class="lg:tw-hidden" @click="handleSaveOrAddToCart" />

        <EditorCloseButton v-if="editorMode === 'addToCart'" class="tw-hidden lg:tw-flex" @close="onCloseClick" />
      </div>
    </header>

    <!-- Main: canvas area, grows to fill space -->
    <main class="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-px-4 lg:tw-px-6 tw-py-0">
      <div ref="sceneContainer"
        class="scene tw-flex-1 tw-flex tw-items-center tw-justify-center tw-min-h-0 tw-bg-bgScene tw-rounded-[32px] tw-overflow-hidden tw-relative"
        :class="{
          'tw-cursor-grab': canPanCard && !isPanning,
          'tw-cursor-grabbing': canPanCard && isPanning,
          'scene--panel-open': mobilePanelOpen,
        }" :title="canPanCard ? $t('scenePanTitle') : ''" @mousedown.capture="onSceneMouseDown"
        @touchstart="onSceneTouchStart">
        <div ref="cardPanWrapper" class="card-pan-wrapper tw-inline-block" :style="cardPanStyle">
          <div class="card" :class="{
            elementSelected:
              selectedElement &&
              !selectedElement?.type?.toLowerCase().includes('text'),
            'is-flipped': isFlipped,
            'card--panel-open': mobilePanelOpen,
          }"
            :style="{
              height: cardHeight,
              width: cardWidth,
              margin: '0 auto',
              '--canvas-border-radius': cardBorderRadius,
              ...(mobilePanelOpen && isMobileViewport ? cardPanelOpenOffsetStyle : {}),
            }">
            <canvas id="helperCanvas" :style="{
              transform: isFlipped ? 'rotateX(180deg)' : '',
            }"></canvas>
            <div class="card__face card__face--front tw-z-10">
              <canvas id="frontEditor" class="tw-shadow-xl canvas-rounded"></canvas>
            </div>
            <div class="card__face card__face--back tw-z-10">
              <canvas id="backEditor" class="tw-shadow-xl canvas-rounded"></canvas>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer: side toggle (mobile), Toolbar, Add to Cart (desktop) -->
    <footer
      class="tw-flex-shrink-0 tw-px-4 lg:tw-px-6 tw-py-[7px] lg:tw-py-[11px] tw-flex tw-flex-col tw-gap-3 lg:tw-gap-0 lg:tw-block">
      <!-- Mobile: Card side toggle with preview in footer -->
      <div class="tw-flex tw-justify-center lg:tw-hidden">
        <SideToggleWithPreview :front-preview-url="previewFrontUrl" :back-preview-url="previewBackUrl"
          :is-flipped="isFlipped" @select-front="showFrontSide" @select-back="showBackSide" />
      </div>

      <div class="tw-flex tw-items-center tw-w-full">
        <Toolbar ref="toolbar" class="tw-flex-1 tw-min-w-0" :isFlipped="isFlipped" :isPanelOpen="mobilePanelOpen"
          :selectedElement="selectedElement" :helperElement="helperElement" :config="config" :token="token"
          @panel-open-change="mobilePanelOpen = $event" @onTemplateSelected="applyTemplate"
          @onStickerSelected="onStickerSelected" @preview-update-needed="schedulePreviewUpdate">
          <template #undoRedo>
            <UndoRedo :isFlipped="isFlipped" :undoFrontHasStates="undoFrontHasStates"
              :undoBackHasStates="undoBackHasStates"
              :defaultBackgroundColor="(isFlipped ? config?.backConfig?.canvas?.backgroundColor : config?.frontConfig?.canvas?.backgroundColor) || '#fff'"
              @undo="undo" @redo="redo" />
          </template>
          <template #scaleSlider>
            <input type="range" v-model="canvasScale" min="0.5" max="2" step="0.1" @input="scaleCanvas"
              @change="onScaleSliderChange" :style="{
                '--scale-progress': `${((canvasScale - 0.5) / (2 - 0.5)) * 100}%`,
              }" class="scaleRangeSlider" />
          </template>
          <template #footerRight>
            <SplitButton v-if="showActions">
              <div v-if="!showImporting" class="tw-p-4 hover:tw-bg-gray-300 tw-cursor-pointer"
                @click="showImporting = true">
                {{ $t("import") }}
              </div>
              <div v-else class="tw-p-4">
                <div class="tw-flex tw-flex-col">
                  <input
                    class="form-input tw-w-[90%] tw-rounded-md tw-py-1 tw-px-2 tw-border tw-border-solid tw-border-solYellow tw-text-sm tw-text-gray-600 tw-mb-2"
                    type="text" v-model="importURL" :placeholder="$t('designID')" />
                  <div class="tw-flex tw-space-x-2">
                    <button
                      class="tw-bg-solGray tw-text-white tw-font-semibold tw-border-none tw-rounded-md tw-px-2 tw-py-1 tw-text-xs tw-cursor-pointer tw-flex-grow"
                      @click="handleImport">
                      {{ $t("import") }}
                    </button>
                    <button
                      class="tw-bg-gray-200 tw-text-gray-800 tw-border-none tw-rounded-md tw-px-2 tw-py-1 tw-text-xs tw-cursor-pointer"
                      @click="showImporting = false">
                      {{ $t("cancel") }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="!isEditOrder" class="tw-p-4 hover:tw-bg-gray-300 tw-cursor-pointer" @click="duplicateDesign">
                {{ $t("duplicateDesign") }}
              </div>
              <div class="tw-p-4 hover:tw-bg-gray-300 tw-cursor-pointer" @click="$refs.pngModal.open">
                {{ $t("download") }}
              </div>
            </SplitButton>
            <SaveOrAddToCartButton :text="saveButtonText" class="tw-hidden lg:tw-flex tw-flex-shrink-0"
              @click="handleSaveOrAddToCart" />
          </template>
        </Toolbar>
      </div>
    </footer>

    <!-- Modals & Components -->
    <Loader ref="loader" />
    <Toast ref="toast" />
    <ConfirmModal ref="leaveModal" :title="$t('leaveCustomizerTitle')"
      :descriptions="[$t('leaveCustomizerDescription'), $t('leaveCustomizerConfirm')]"
      :primary-button-label="$t('exit')" :secondary-button-label="$t('keepEditing')" @onConfirm="handleClose"
      @onCancel="onLeaveModalDismissed" />
    <PromptModal :subtitle-text="$t('unsavedChangesP')" :title-text="$t('unsavedChanges')"
      :button-confirm-text="$t('saveCurrentDesign')" :button-close-text="$t('continueWithoutSaving')" ref="promptModal"
      @onModalClosed="onPromptModalClosed" />
    <DownloadPngModal ref="pngModal" @onDownloadPng="downloadPNG" @onDownloadPdf="downloadPDF" />
    <!-- DesignSavedModal: legacy usecase (email/QR for standalone designer) - COMMENTED OUT -->
    <!-- <DesignSavedModal ref="designSavedModal" :job-id="jobID" :show-q-r-code="true"
      :notification-sent="notificationSent" /> -->
    <ReviewDesignModal ref="reviewDesignModal"
      :product-name="config?.type === 'card' ? 'Shield' : (config?.type || 'Shield')"
      :primary-button-label="reviewModalConfirmButtonLabel"
      :front-background-color="config?.frontConfig?.canvas?.backgroundColor"
      :back-background-color="config?.backConfig?.canvas?.backgroundColor" @onConfirm="doSaveAndProceed"
      @onClose="onReviewModalRefineDesign" />

    <!-- Products Panel for Order Management -->
    <Products v-if="isEditOrder" :visible="productsVisible" :enableSave="enableSave"
      @productsVisible="(v) => (productsVisible = v)" @onProductClick="onProductClick" @copyDesign="onCopyDesign"
      @afterLoad="afterProductsLoad" @pasteSingle="pasteSingle" @pasteToAllAtOnce="pasteToAllAtOnce"
      @saveDesign="saveDesign" @orderSubmitted="orderSubmitted" ref="products" />
  </div>
</template>

<script>
import * as fabric from "fabric";
import { triggerConfetti } from "@/utils/confetti";
import Toolbar from "@/components/panels/Toolbar.vue";
import SplitButton from "@/components/shared/SplitButton.vue";
import SaveOrAddToCartButton from "@/components/shared/SaveOrAddToCartButton.vue";
import SideToggleWithPreview from "@/components/shared/SideToggleWithPreview.vue";
import EditorCloseButton from "@/components/shared/EditorCloseButton.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import Loader from "@/components/shared/Loader.vue";
import PromptModal from "@/components/modals/PromptModal.vue";
import UndoRedo from "@/components/shared/UndoRedo.vue";
import DownloadPngModal from "@/components/modals/DownloadPngModal.vue";
import DesignSavedModal from "@/components/modals/DesignSavedModal.vue";
import ReviewDesignModal from "@/components/modals/ReviewDesignModal.vue";
import Toast from "@/components/shared/Toast.vue";
import Products from "@/components/order/Products.vue";
import allFonts from "@/constants/allFonts";
import isUUID from "@/utils/helpers/isUUID";
import debounce from "@/utils/helpers/debounce";
import { getCardDesign, getTemplate } from "@/services/api";
import { getAssetPath } from "@/utils/assetPath";
import { trackEvent, dispatchHostEvent } from "@/utils/tracking";

import { getCanvasBorderRadiusPx } from "@/constants/canvas";
import canvasSetupMixin from "@/mixins/editor/canvasSetupMixin";
import canvasManipulationMixin from "@/mixins/editor/canvasManipulationMixin";
import elementSelectionMixin from "@/mixins/editor/elementSelectionMixin";
import designManagementMixin from "@/mixins/editor/designManagementMixin";
import productManagementMixin from "@/mixins/editor/productManagementMixin";

export default {
  mixins: [
    canvasSetupMixin,
    canvasManipulationMixin,
    elementSelectionMixin,
    designManagementMixin,
    productManagementMixin,
  ],

  components: {
    Toolbar,
    SplitButton,
    SaveOrAddToCartButton,
    SideToggleWithPreview,
    EditorCloseButton,
    ConfirmModal,
    Loader,
    PromptModal,
    UndoRedo,
    DownloadPngModal,
    DesignSavedModal,
    ReviewDesignModal,
    Toast,
    Products,
  },

  props: {
    editorMode: {
      type: String,
      default: "save", // 'save' or 'addToCart'
    },
    metadata: {
      type: Object,
      default: null, // Metadata object with productId, variantId
    },
    confetti: {
      type: Boolean,
      default: false, // Default confetti disabled
    },
    designId: {
      type: String,
      default: null, // Design ID to load
    },
  },

  setup() {
    return {
      getAssetPath,
    };
  },

  data() {
    return {
      jobID: null,
      selectedElement: null,
      token: null,
      showActions: false,
      helperElement: null,
      config: {},
      isFlipped: false,
      canvasScale: 1,
      productsVisible: false,
      numberOfProducts: 0,
      fontsToLoad: [...allFonts],
      undoFrontHasStates: false,
      undoBackHasStates: false,
      isSubmitted: false,
      initialized: false,
      importURL: "",
      showImporting: false,
      showDownloadSubmenu: false,
      notificationSent: false,
      mobilePanelOpen: false,
      previewFrontUrl: null,
      previewBackUrl: null,
      cardPanX: 0,
      cardPanY: 0,
      isPanning: false,
      panStartClientX: 0,
      panStartClientY: 0,
      panStartCardX: 0,
      panStartCardY: 0,
      spaceKeyHeld: false,
      cardOverflowsScene: false,
      isMobileViewport: false,
      cardPanelOpenOffset: null,
      cardPanelLiftPx: 0,
      saveInProgress: false,
    };
  },

  computed: {
    queryParams() {
      return new URL(window.location.href).searchParams;
    },
    isEditOrder() {
      let path = location.pathname.substring(1);
      return path.startsWith("order/");
    },
    cardWidth() {
      const w = this.canvasWidthPx ?? window.frontCanvas?.getWidth();
      return w != null ? `${w}px !important` : "850px !important";
    },
    cardHeight() {
      const h = this.canvasHeightPx ?? window.frontCanvas?.getHeight();
      return h != null ? `${h}px !important` : "534px !important";
    },
    cardBorderRadius() {
      const w = this.canvasWidthPx ?? window.frontCanvas?.getWidth();
      return `${getCanvasBorderRadiusPx(w)}px`;
    },
    enableSave() {
      return (
        (this.undoFrontHasStates || this.undoBackHasStates) && !this.isSubmitted
      );
    },
    cardPanStyle() {
      const pan = `translate(${this.cardPanX}px, ${this.cardPanY}px)`;
      const liftPx = this.mobilePanelOpen && this.isMobileViewport ? this.cardPanelLiftPx : 0;
      const lift = liftPx > 0 ? ` translateY(-${liftPx}px)` : "";
      return {
        transform: pan + lift,
        transition: this.isPanning ? "none" : "transform 500ms ease-out",
      };
    },
    canPanCard() {
      return this.cardOverflowsScene && window.frontCanvas;
    },
    saveButtonText() {
      if (this.editorMode === "addToCart") {
        return this.designId
          ? this.$t("updateDesign")
          : this.$t("addToCart");
      }
      return this.$t("save");
    },
    reviewModalConfirmButtonLabel() {
      // Same as saveButtonText: Save | Add to Cart | Update Design
      return this.saveButtonText;
    },
    cardPanelOpenOffsetStyle() {
      return this.cardPanelOpenOffset != null
        ? { "--card-panel-open-offset": this.cardPanelOpenOffset }
        : {};
    },
  },

  watch: {
    initialized(val) {
      if (val && typeof this.checkCardOverflow === "function") {
        this.$nextTick(() => {
          this.checkCardOverflow();
          this.updateCardPanelOpenOffset();
        });
      }
    },
    mobilePanelOpen(isOpen) {
      if (!isOpen || !this.isMobileViewport) return;
      const run = () => this.updateCardPanelOpenOffset();
      this.$nextTick(run);
      requestAnimationFrame(() => requestAnimationFrame(run));
      [100, 350, 550].forEach((ms) => setTimeout(run, ms));
    },
  },

  methods: {
    // Helper methods for undo/redo state
    objectModified() {
      window.frontCanvas.fire("object:modified", {});
    },

    updateFrontUndoState(state) {
      this.undoFrontHasStates = state;
    },

    updateBackUndoState(state) {
      this.undoBackHasStates = state;
    },

    // Utility methods
    fireDebouncedModify() {
      // Will be set in mounted
    },

    timeout(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    updatePreviews() {
      if (!window.frontCanvas || !window.backCanvas) return;
      try {
        this.previewFrontUrl = window.frontCanvas.toDataURL({
          format: "jpeg",
          quality: 0.5,
          multiplier: 0.12,
        });
        this.previewBackUrl = window.backCanvas.toDataURL({
          format: "jpeg",
          quality: 0.5,
          multiplier: 0.12,
        });
      } catch (e) {
        // ignore export errors (e.g. canvas not ready)
      }
    },

    schedulePreviewUpdate() {
      if (this._previewUpdateScheduled) return;
      this._previewUpdateScheduled = true;
      requestAnimationFrame(() => {
        this.updatePreviews();
        this._previewUpdateScheduled = false;
      });
    },

    updateCardPanelOpenOffset() {
      if (!this.isMobileViewport || !this.mobilePanelOpen) return;
      const scene = this.$refs.sceneContainer;
      if (!scene) {
        setTimeout(() => this.updateCardPanelOpenOffset(), 50);
        return;
      }
      const main = scene.parentElement;
      if (!main) return;
      const sceneRect = scene.getBoundingClientRect();
      const mainRect = main.getBoundingClientRect();
      const cardHeightPx =
        this.canvasHeightPx ??
        window.frontCanvas?.getHeight() ??
        534;
      const cardTopNow = sceneRect.top + sceneRect.height / 2 - cardHeightPx / 2;
      const marginTopPx = 16;
      const liftPx = Math.max(0, cardTopNow - mainRect.top - marginTopPx);
      this.cardPanelLiftPx = Math.round(liftPx);
      this.cardPanelOpenOffset = "80px";
    },

    clickedOnFabricObject(e) {
      const canvas = window.activeCanvas;
      if (!canvas) return false;
      try {
        const pointer = canvas.getPointer(e);
        const objs = canvas.getObjects().slice().reverse();
        for (const obj of objs) {
          if (obj.containsPoint && obj.containsPoint(new fabric.Point(pointer.x, pointer.y))) return true;
        }
      } catch (_) { }
      return false;
    },
    onSceneMouseDown(e) {
      if (!this.canPanCard || e.button !== 0) return;

      // Space held = always allow pan (like Photoshop/Figma)
      if (this.spaceKeyHeld) {
        e.preventDefault();
        e.stopPropagation();
        this.isPanning = true;
        this.panStartClientX = e.clientX;
        this.panStartClientY = e.clientY;
        this.panStartCardX = this.cardPanX;
        this.panStartCardY = this.cardPanY;
        document.addEventListener("mousemove", this.onPanMouseMove);
        document.addEventListener("mouseup", this.onPanMouseUp);
        return;
      }

      // Click on element (image, text, etc.) = don't pan, let Fabric handle drag
      if (this.clickedOnFabricObject(e)) return;

      // Empty background = allow pan
      e.preventDefault();
      e.stopPropagation();
      this.isPanning = true;
      this.panStartClientX = e.clientX;
      this.panStartClientY = e.clientY;
      this.panStartCardX = this.cardPanX;
      this.panStartCardY = this.cardPanY;
      document.addEventListener("mousemove", this.onPanMouseMove);
      document.addEventListener("mouseup", this.onPanMouseUp);
    },
    onPanMouseMove(e) {
      if (!this.isPanning) return;
      e.preventDefault();
      const dx = e.clientX - this.panStartClientX;
      const dy = e.clientY - this.panStartClientY;
      let newX = this.panStartCardX + dx;
      let newY = this.panStartCardY + dy;
      const scene = this.$refs.sceneContainer;
      const cardW = window.frontCanvas?.getWidth() || 0;
      const cardH = window.frontCanvas?.getHeight() || 0;
      if (scene && cardW && cardH) {
        const sceneW = scene.clientWidth;
        const sceneH = scene.clientHeight;
        const maxX = Math.max(0, (cardW - sceneW) / 2);
        const maxY = Math.max(0, (cardH - sceneH) / 2);
        newX = Math.max(-maxX, Math.min(maxX, newX));
        newY = Math.max(-maxY, Math.min(maxY, newY));
      }
      this.cardPanX = newX;
      this.cardPanY = newY;
    },
    onPanMouseUp() {
      this.isPanning = false;
      document.removeEventListener("mousemove", this.onPanMouseMove);
      document.removeEventListener("mouseup", this.onPanMouseUp);
    },
    onSceneTouchStart(e) {
      if (!this.canPanCard || e.touches.length !== 2) return;
      e.preventDefault();
      this.isPanning = true;
      this.panStartClientX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      this.panStartClientY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      this.panStartCardX = this.cardPanX;
      this.panStartCardY = this.cardPanY;
      document.addEventListener("touchmove", this.onPanTouchMove, { passive: false });
      document.addEventListener("touchend", this.onPanTouchEnd);
    },
    onPanTouchMove(e) {
      if (!this.isPanning || e.touches.length !== 2) return;
      e.preventDefault();
      const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const dx = cx - this.panStartClientX;
      const dy = cy - this.panStartClientY;
      let newX = this.panStartCardX + dx;
      let newY = this.panStartCardY + dy;
      const scene = this.$refs.sceneContainer;
      const cardW = window.frontCanvas?.getWidth() || 0;
      const cardH = window.frontCanvas?.getHeight() || 0;
      if (scene && cardW && cardH) {
        const sceneW = scene.clientWidth;
        const sceneH = scene.clientHeight;
        const maxX = Math.max(0, (cardW - sceneW) / 2);
        const maxY = Math.max(0, (cardH - sceneH) / 2);
        newX = Math.max(-maxX, Math.min(maxX, newX));
        newY = Math.max(-maxY, Math.min(maxY, newY));
      }
      this.cardPanX = newX;
      this.cardPanY = newY;
    },
    onPanTouchEnd(e) {
      if (e.touches.length < 2) {
        this.isPanning = false;
        document.removeEventListener("touchmove", this.onPanTouchMove);
        document.removeEventListener("touchend", this.onPanTouchEnd);
      }
    },
    onSceneWheel(e) {
      if (!this.canPanCard) return;
      e.preventDefault();
      const deltaX = e.deltaX ?? 0;
      const deltaY = e.deltaY ?? 0;
      if (deltaX === 0 && deltaY === 0) return;
      let newX = this.cardPanX - deltaX;
      let newY = this.cardPanY - deltaY;
      const scene = this.$refs.sceneContainer;
      const cardW = window.frontCanvas?.getWidth() || 0;
      const cardH = window.frontCanvas?.getHeight() || 0;
      if (scene && cardW && cardH) {
        const sceneW = scene.clientWidth;
        const sceneH = scene.clientHeight;
        const maxX = Math.max(0, (cardW - sceneW) / 2);
        const maxY = Math.max(0, (cardH - sceneH) / 2);
        newX = Math.max(-maxX, Math.min(maxX, newX));
        newY = Math.max(-maxY, Math.min(maxY, newY));
      }
      this.cardPanX = newX;
      this.cardPanY = newY;
    },
    resetCardPan() {
      this.cardPanX = 0;
      this.cardPanY = 0;
    },
    checkCardOverflow() {
      const scene = this.$refs.sceneContainer;
      const cardW = window.frontCanvas?.getWidth() || 0;
      const cardH = window.frontCanvas?.getHeight() || 0;
      if (!scene || !cardW || !cardH) {
        this.cardOverflowsScene = false;
        return;
      }
      this.cardOverflowsScene = cardW > scene.clientWidth || cardH > scene.clientHeight;
    },
    onKeyDown(e) {
      if (e.code === "Space" && !e.repeat && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        this.spaceKeyHeld = true;
      }
    },
    onKeyUp(e) {
      if (e.code === "Space" && !e.repeat) {
        this.spaceKeyHeld = false;
        if (this.isPanning) this.onPanMouseUp();
      }
    },
    setupPreviewListeners() {
      const schedule = () => this.schedulePreviewUpdate();
      if (window.frontCanvas) {
        window.frontCanvas.on("object:modified", schedule);
        window.frontCanvas.on("object:added", schedule);
        window.frontCanvas.on("object:removed", schedule);
        window.frontCanvas.on("text:changed", schedule);
      }
      if (window.backCanvas) {
        window.backCanvas.on("object:modified", schedule);
        window.backCanvas.on("object:added", schedule);
        window.backCanvas.on("object:removed", schedule);
        window.backCanvas.on("text:changed", schedule);
      }
    },

    onScaleSliderChange() {
      const scale = Number(this.canvasScale);
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "ScaleCanvas",
        details: { scale: isFinite(scale) ? Math.round(scale * 10) / 10 : this.canvasScale },
      });
    },

    handlePaste(e) {
      this.$refs.toolbar.handlePaste(e);
    },

    // Undo/Redo implementation (to be implemented)
    undo() {
      console.log("Undo");
    },

    redo() {
      console.log("Redo");
    },

    // Handle Save or Add to Cart - show review modal first
    handleSaveOrAddToCart() {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "ReviewDesignOpened",
      });
      this.$refs.reviewDesignModal.open();
    },

    // Called when user confirms in ReviewDesignModal - performs actual save
    async doSaveAndProceed() {
      if (this.saveInProgress) return;
      this.saveInProgress = true;
      try {
        await this.saveDesign();

        if (this.editorMode === "addToCart") {
        // Extract front canvas as jpeg in base64
        await window?.frontCanvas?.discardActiveObject();
        await window?.frontCanvas?.renderAll();

        const frontImageBase64 = window.frontCanvas.toDataURL({
          format: "jpeg",
          quality: 0.8,
          multiplier: 0.4,
          enableRetinaScaling: false,
        });

        // Conditionally trigger confetti effect based on prop
        if (this.confetti) {
          triggerConfetti();
          // Small delay to let confetti start before hiding
          await this.timeout(300);
        }

        // Hide editor and emit event
        window.hideEditor();

        const eventDetail = {
          designId: this.jobID,
          image_preview: frontImageBase64,
        };

        // Add metadata if it exists
        if (this.metadata) {
          eventDetail.metadata = this.metadata;
        }

        // Dispatch different event based on whether it's edit or new design
        const eventName = this.designId
          ? "solflareDesignUpdated"  // Editing existing design
          : "solflareAddToCartCompleted";  // Creating new design

        trackEvent({
          eventName: "DesignCompleted",
          eventType: "Submit",
          action: "AddToCartCompleted",
          details: { designId: this.jobID },
        });

        dispatchHostEvent(eventName, eventDetail);
        }
      } finally {
        this.saveInProgress = false;
      }
    },

    onCloseClick() {
      if (this.editorMode === "addToCart") {
        this.$refs.leaveModal.open();
      }
    },

    onLeaveModalDismissed() {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "CloseModal",
        details: { modal: "LeaveModal" },
      });
    },

    onReviewModalRefineDesign() {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "RefineDesignClicked",
        details: { modal: "ReviewDesignModal" },
      });
    },

    // Handle Close in addToCart mode
    handleClose() {
      if (this.editorMode === "addToCart") {
        trackEvent({
          eventName: "Customizer",
          eventType: "Click",
          action: "EditorClosed",
        });
        // Hide editor without saving
        window.hideEditor();

        dispatchHostEvent("solflareCustomizerClosed");
      }
    },

    // Handle sticker selection
    async onStickerSelected(sticker) {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "SelectSticker",
        details: sticker?.id ? { stickerId: sticker.id } : undefined,
      });
      try {
        const img = await window.fabric.FabricImage.fromURL(sticker.url, {
          crossOrigin: "anonymous",
        });

        img.set({
          cornerStyle: "circle",
        });

        const controlsVisibility =
          await import("@/constants/controlsVisibility");
        img.setControlsVisibility(controlsVisibility.default);
        img.scaleToWidth(200);

        window.activeCanvas.viewportCenterObject(img);
        window.activeCanvas.add(img);
        window.activeCanvas.setActiveObject(img);
        window.activeCanvas.renderAll();

        this.fireDebouncedModify();
      } catch (error) {
        console.error("Error adding sticker:", error);
      }
    },
  },

  created() {
    window.fabric = fabric;
    this.canvasScale = 1;
  },

  async mounted() {
    this.$refs.loader.show(this.$t("loading"));

    this.fireDebouncedModify = debounce(() => {
      window.activeCanvas.fire("object:modified");
    }, 500);

    const queryParams = new URL(location.href).searchParams;

    // Check for actions in URL or localStorage
    const actionsFromUrl = queryParams.get("actions");
    const actionsFromStorage = localStorage.getItem("actions");
    this.showActions =
      actionsFromUrl === "true" || actionsFromStorage === "true";

    // Store in localStorage if present in URL
    if (actionsFromUrl === "true") {
      localStorage.setItem("actions", "true");
    }

    this.addGlobals();

    window.addEventListener("resize", this.handleAppResize);
    this.updateMobileViewport = () => {
      this.isMobileViewport = window.innerWidth < 1024;
      this.updateCardPanelOpenOffset();
    };
    this.updateMobileViewport();
    window.addEventListener("resize", this.updateMobileViewport);
    window.addEventListener("paste", this.handlePaste);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.$nextTick(() => {
      const scene = this.$refs.sceneContainer;
      if (scene) {
        scene.addEventListener("wheel", this.onSceneWheel, { passive: false });
      }
      this.updateCardPanelOpenOffset();
    });

    // Check if designId is provided via prop (from window.showEditor)
    if (this.designId) {
      // Load design from designId prop
      this.jobID = this.designId;
      const data = await getCardDesign(this.designId);

      // Set notificationSent flag if present in the loaded design
      if (data && data.notificationSent !== undefined) {
        this.notificationSent = data.notificationSent;
      }

      await this.initializeCanvases(true, data);

      if (this.isEditOrder) {
        this.productsVisible = true;
        this.$refs.loader.show();
      }
      return;
    }

    // If no designId prop, use URL-based logic
    const path = location.pathname.substring(1);
    const onlyInitializeCanvas = path.startsWith("import") || isUUID(path);

    // Handle order path or empty path - initialize with defaults
    if (!path || path.startsWith("order/")) {
      await this.initializeCanvases(false);
      return;
    }

    // Extract job ID from path
    let jobId = null;

    if (path.startsWith("import")) {
      jobId = path.split("/")[1];
      // Clean URL by removing the import path
      const newURL = window.location.href.replace(window.location.pathname, "");
      window.history.replaceState(null, null, newURL);
    } else if (isUUID(path)) {
      this.jobID = path;
      jobId = path;
    }

    // Load design data based on path type
    let data = null;

    if (path.startsWith("template")) {
      const templateId = path.split("/")[1];
      const template = await getTemplate(templateId, "print");
      data = template.config;
    } else if (jobId) {
      data = await getCardDesign(jobId);
      // Set notificationSent flag if present in the loaded design
      if (data && data.notificationSent !== undefined) {
        this.notificationSent = data.notificationSent;
      }
    }

    await this.initializeCanvases(onlyInitializeCanvas, data);

    if (this.isEditOrder) {
      this.productsVisible = true;
      this.$refs.loader.show();
    }
  },

  beforeUnmount() {
    if (this.updateMobileViewport) {
      window.removeEventListener("resize", this.updateMobileViewport);
    }
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    document.removeEventListener("mousemove", this.onPanMouseMove);
    document.removeEventListener("mouseup", this.onPanMouseUp);
    document.removeEventListener("touchmove", this.onPanTouchMove);
    document.removeEventListener("touchend", this.onPanTouchEnd);
    const scene = this.$refs.sceneContainer;
    if (scene) {
      scene.removeEventListener("wheel", this.onSceneWheel);
    }
  },
};
</script>

<style lang="scss">
#cardDesigner {
  position: fixed !important;
  width: 100vw;
  height: 100dvh;
  z-index: 99999999 !important;
  top: 0;
  left: 0;
  background: white !important;
  -webkit-tap-highlight-color: transparent;

  .helperCanvas {
    display: none;
    position: absolute !important;
    margin: 0 auto;
    z-index: 4;

    canvas {
      z-index: 5;
    }

    .upper-canvas {
      z-index: 6;
    }
  }

  .elementSelected .helperCanvas {
    z-index: 14;

    canvas {
      z-index: 15;
    }

    .upper-canvas {
      z-index: 16;
    }
  }

  & * {
    font-family: "FKGrotesk";
  }

  .card.is-flipped .card__face--back {
    z-index: 20;
  }

  button {
    background-image: unset !important;
    font-weight: 500 !important;
  }

  .scene {
    perspective: 1600px;
    border: unset;

    &.tw-cursor-grab {
      cursor: grab !important;
    }

    &.tw-cursor-grab:not(.tw-cursor-grabbing) .card-pan-wrapper {
      cursor: default;
    }

    &.tw-cursor-grabbing {
      cursor: grabbing !important;
    }

    &.tw-cursor-grabbing .card-pan-wrapper {
      cursor: grabbing !important;
    }
  }

  .card {
    width: 100% !important;
    height: 100% !important;
    position: relative !important;
    transition: transform 1s;
    transform-style: preserve-3d;
    transform-origin: center center;

  }

  .card__face {
    position: absolute !important;
    backface-visibility: hidden !important;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    @media (max-width: 1023px) {
      transition: top 500ms ease-out;
    }
  }

  @media (max-width: 1023px) {

    .scene.scene--panel-open {
      overflow: visible !important;
    }

    .editor-grid.editor--panel-open {
      overflow: visible !important;
    }
  }

  .canvas-container {
    margin: 0 auto;
    border-radius: var(--canvas-border-radius, 25px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  }

  .canvas-rounded {
    border-radius: var(--canvas-border-radius, 25px);
  }

  .card__face--back {
    transform: translate(-50%, -50%) rotateX(180deg);
  }

  .card.is-flipped {
    transform: rotateX(180deg);
  }

  .card.is-flipped .card__face--front {
    transform: translate(-50%, -50%);
  }

  .card.is-flipped .card__face--back {
    transform: translate(-50%, -50%) rotateX(180deg);
  }

  .card__face--back {
    pointer-events: none;
  }

  .is-flipped .card__face--back {
    pointer-events: all;
  }

  .card__face--front {
    pointer-events: all;
  }

  .is-flipped .card__face--front {
    pointer-events: none;
    z-index: 1;
  }

  .colorPicker {
    width: 100vw !important;
    background: white;

    .vc-chrome {
      width: calc(100% - 40px);
      box-shadow: none;
      margin: 0 20px;
      background: transparent !important;

      .vc-chrome-saturation-wrap {
        border-radius: 10px;
        border: 2px solid black;
      }

      .vc-chrome-toggle-icon {
        margin-top: 2px;
      }
    }
  }

  .toolbar {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .actionIcon {
    width: 50px;
    height: 50px;
    position: absolute;
    top: 45%;
    left: calc(50% - 25px);
  }

  .colorPickerHidden {
    transform: translateY(1000px) !important;
  }

  // Side Toggle Buttons
  .side-toggle-container {
    display: inline-flex;
    background: var(--color-bg-dark);
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .side-toggle-button {
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    font-weight: 700;
    font-size: 15px;
    padding: 0 28px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 110px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;

    &:hover:not(.active) {
      color: var(--color-text-muted-light);
      background: rgba(255, 255, 255, 0.05);
    }

    &.active {
      background: var(--color-bg-scale-track);
      color: var(--color-white);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }

  @media (min-width: 1024px) {
    .cardRotate {
      margin-top: 0 !important;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .activeSide {
    color: white;
    background-color: var(--color-sol-black);
  }
}

.productsContainer {
  width: 350px;
  transition: all 0.5s;
  left: -400px;

  &.visible {
    left: 0;
  }
}
</style>
